import {
  ExternalRouteRef,
  RouteRef,
  SubRouteRef,
  createExternalRouteRef,
} from '@backstage/core-plugin-api';

import { createPageRoute } from '@spotify/confidence-common-react';
import {
  ResourceAliasMap,
  buildResourceAliasMap,
} from '@spotify/confidence-core-react';

export const metricsExternalRouteRef = createExternalRouteRef({
  id: 'metrics',
  optional: true,
});

export const userSettingsExternalRouteRef = createExternalRouteRef({
  id: 'user-settings',
  optional: true,
});

export const issueExternalRouteRef = createExternalRouteRef({
  id: 'issue',
  params: ['id'],
  optional: true,
});

export const adminRouteRef = createPageRoute({
  id: 'admin',
  page: { title: 'Admin' },
});

export const rootRouteRef = adminRouteRef;

export const resolverServiceConfigurationsRouteRef = createPageRoute({
  id: 'admin-resolver-service-configurations',
  route: {
    path: '/resolver-service-configurations',
    parent: rootRouteRef,
  },
  page: { title: 'Self-hosted resolver configurations' },
});

export const slackRouteRef = createPageRoute({
  id: 'admin-notifications',
  route: { path: '/slack', parent: rootRouteRef },
  page: { title: 'Slack' },
});

export const policiesRouteRef = createPageRoute({
  id: 'admin-policy-index',
  route: { path: '/policies', parent: rootRouteRef },
  page: { title: 'Policies' },
});

export const groupsRouteRef = createPageRoute({
  id: 'admin-group-index',
  route: { path: '/groups', parent: rootRouteRef },
  page: { title: 'Groups' },
});

export const groupRouteRef = createPageRoute({
  id: 'admin-group',
  route: { path: '/groups/:id', parent: groupsRouteRef },
  page: { title: 'Group' },
});

export const usersRouteRef = createPageRoute({
  id: 'admin-user-index',
  route: { path: '/users', parent: rootRouteRef },
  page: { title: 'Users', defaultTab: 'Active users' },
});

export const domainsRouteRef = createPageRoute({
  id: 'admin-domain-index',
  route: { path: '/domains', parent: rootRouteRef },
  page: { title: 'Domains' },
});

export const invitationsRouteRef = createPageRoute({
  id: 'admin-user-invitations',
  route: { path: '/users/invitations', parent: usersRouteRef },
  page: { title: 'Pending invitations' },
});

export const userRouteRef = createPageRoute({
  id: 'admin-user',
  route: { path: '/users/:id', parent: usersRouteRef },
  page: { title: 'User' },
});

export const agentsRouteRef = createPageRoute({
  id: 'admin-agent-index',
  route: { path: '/agents', parent: rootRouteRef },
  page: { title: 'Agents' },
});

export const agentRouteRef = createPageRoute({
  id: 'admin-agent',
  route: { path: '/agents/:id', parent: agentsRouteRef },
  page: { title: 'Agent' },
});

export const apiClientsRouteRef = createPageRoute({
  id: 'admin-api-client-index',
  route: { path: '/api-clients', parent: rootRouteRef },
  page: { title: 'API clients' },
});

export const apiClientRouteRef = createPageRoute({
  id: 'admin-api-client',
  route: { path: '/api-clients/:id', parent: apiClientsRouteRef },
  page: { title: 'API client' },
});

export const clientsRouteRef = createPageRoute({
  id: 'admin-clients',
  route: { path: '/clients', parent: rootRouteRef },
  page: { title: 'Clients', defaultTab: 'List' },
});

export const clientRouteRef = createPageRoute({
  id: 'admin-client-detail',
  route: { path: '/clients/:id', parent: clientsRouteRef },
  page: { title: 'Client' },
});

export const clientsContextSchemaRouteRef = createPageRoute({
  id: 'admin-clients-schema',
  route: { path: '/clients/schema', parent: clientsRouteRef },
  page: { title: 'Context schema' },
});

export const rolesRouteRef = createPageRoute({
  id: 'admin-role-index',
  route: { path: '/roles', parent: rootRouteRef },
  page: { title: 'Roles' },
});

export const oauthAppsRouteRef = createPageRoute({
  id: 'admin-oauth-app-index',
  route: { path: '/oauth-apps', parent: rootRouteRef },
  page: { title: 'OAuth apps' },
});

export const oauthAppRouteRef = createPageRoute({
  id: 'admin-oauth-app',
  route: { path: '/oauth-apps/:id', parent: oauthAppsRouteRef },
  page: { title: 'OAuth app' },
});

export const creditUsageRouteRef = createPageRoute({
  id: 'admin-credit-usage',
  route: { path: '/credit-usage', parent: rootRouteRef },
  page: { title: 'Credit usage' },
});

export const metricsDataWarehouseRouteRef = createPageRoute({
  id: 'admin-data-warehouse',
  route: { path: '/metrics-datawarehouse', parent: rootRouteRef },
  page: { title: 'Data warehouse' },
});

export const githubRouteRef = createPageRoute({
  id: 'admin-github',
  route: { path: '/github', parent: rootRouteRef },
  page: { title: 'GitHub' },
});

export const cursorIntegrationRouteRef = createPageRoute({
  id: 'admin-cursor',
  route: { path: '/cursor-integration', parent: rootRouteRef },
  page: { title: 'Cursor' },
});

export const claudeAgentsIntegrationRouteRef = createPageRoute({
  id: 'admin-claude-agents',
  route: { path: '/claude-agents-integration', parent: rootRouteRef },
  page: { title: 'Claude' },
});

export const managedCodingAgentRouteRef = createPageRoute({
  id: 'admin-managed-coding-agent',
  route: { path: '/managed-coding-agent', parent: rootRouteRef },
  page: { title: 'Confidence Code' },
});

export const promptHooksRouteRef = createPageRoute({
  id: 'admin-prompt-hooks',
  route: { path: '/prompt-hooks', parent: rootRouteRef },
  page: { title: 'Prompt hooks' },
});

export const promptHookRouteRef = createPageRoute({
  id: 'admin-prompt-hook',
  route: { path: '/prompt-hooks/:id', parent: promptHooksRouteRef },
  page: { title: 'Prompt hook' },
});

export const promptHookDocumentRouteRef = createPageRoute({
  id: 'admin-prompt-hook-document',
  route: {
    path: '/prompt-hooks/:id/documents/:documentId',
    parent: promptHookRouteRef,
  },
  page: { title: 'Scratchpad' },
});

export const workflowsRouteRef = createPageRoute({
  id: 'admin-workflow-index',
  route: { path: '/workflows', parent: rootRouteRef },
  page: { title: 'Workflows' },
});

export const workflowRouteRef = createPageRoute({
  id: 'admin-workflow',
  route: { path: '/workflows/:id', parent: workflowsRouteRef },
  page: { title: 'Workflow', defaultTab: 'Overview' },
});

export const workflowInstancesRouteRef = createPageRoute({
  id: 'admin-workflow-instance-index',
  route: { path: '/workflows/:id/instances', parent: workflowRouteRef },
  page: { title: 'Instances' },
});

export const workflowInstanceOverviewRouteRef = createPageRoute({
  id: 'admin-workflow-instance',
  route: {
    path: '/workflows/:id/instances/:instance',
    parent: workflowInstancesRouteRef,
  },
  page: { title: 'Workflow instance', defaultTab: 'Overview' },
});

export const workflowInstanceEventsRouteRef = createPageRoute({
  id: 'admin-workflow-instance-events',
  route: {
    path: '/workflows/:id/instances/:instance/events',
    parent: workflowInstanceOverviewRouteRef,
  },
  page: { title: 'Events' },
});

export const workflowLogsRouteRef = createPageRoute({
  id: 'admin-workflow-logs',
  route: { path: '/workflows/:id/logs', parent: workflowRouteRef },
  page: { title: 'Logs' },
});

export const workflowTunnelsRouteRef = createPageRoute({
  id: 'admin-workflow-tunnels',
  route: { path: '/workflows/:id/tunnels', parent: workflowRouteRef },
  page: { title: 'Dev tunnels' },
});

export const flagAppliedConnectionsRouteRef = createPageRoute({
  id: 'admin-flag-applied-index',
  route: { path: '/connections/flag-applied', parent: rootRouteRef },
  page: { title: 'Flag applied connections' },
});

export const flagAppliedConnectionRouteRef = createPageRoute({
  id: 'admin-flag-applied-connection',
  route: {
    path: '/connections/flag-applied/:id',
    parent: flagAppliedConnectionsRouteRef,
  },
  page: { title: 'Flag-applied connection' },
});

export const eventConnectionsRouteRef = createPageRoute({
  id: 'admin-event-connection-index',
  route: { path: '/connections/event', parent: rootRouteRef },
  page: { title: 'Event connections' },
});

export const eventConnectionRouteRef = createPageRoute({
  id: 'admin-event-connection',
  route: {
    path: '/connections/event/:id',
    parent: eventConnectionsRouteRef,
  },
  page: { title: 'Event connection' },
});

export const activityFeedsRouteRef = createPageRoute({
  id: 'admin-activity-feeds',
  route: { path: '/activity-feeds', parent: rootRouteRef },
  page: { title: 'Activity feeds' },
});

export const environmentsRouteRef = createPageRoute({
  id: 'admin-environments',
  route: { path: '/environments', parent: rootRouteRef },
  page: { title: 'Environments' },
});

export const dataTablesRouteRef = createPageRoute({
  id: 'admin-data-tables',
  route: { path: '/data-tables', parent: rootRouteRef },
  page: { title: 'Data tables' },
});

export const dataTablesAssignmentTablesRouteRef = createPageRoute({
  id: 'admin-data-tables-assignment-tables',
  route: { path: '/data-tables/assignment-tables', parent: dataTablesRouteRef },
  page: { title: 'Assignment tables', defaultTab: 'Assignment tables' },
});

export const dataTablesFactTablesRouteRef = createPageRoute({
  id: 'admin-data-tables-fact-tables',
  route: { path: '/data-tables/fact-tables', parent: dataTablesRouteRef },
  page: { title: 'Fact tables', defaultTab: 'Fact tables' },
});

export const dataTablesDimensionTablesRouteRef = createPageRoute({
  id: 'admin-data-tables-dimension-tables',
  route: {
    path: '/data-tables/dimension-tables',
    parent: dataTablesRouteRef,
  },
  page: { title: 'Dimension tables', defaultTab: 'Dimension tables' },
});

export const assignmentTablesRouteRef = createPageRoute({
  id: 'admin-assignment-table-index',
  route: { path: '/assignment-tables', parent: rootRouteRef },
  page: { title: 'Assignment tables' },
});

export const assignmentTableRouteRef = createPageRoute({
  id: 'admin-assignment-table',
  route: { path: '/assignment-tables/:id', parent: assignmentTablesRouteRef },
  page: { title: 'Assignment table' },
});

export const assignmentTablesEditRouteRef = createPageRoute({
  id: 'admin-assignment-table-edit',
  route: {
    path: '/assignment-tables/:id/edit',
    parent: assignmentTableRouteRef,
  },
  page: { title: 'Edit assignment table' },
});

export const assignmentTablesCreateRouteRef = createPageRoute({
  id: 'admin-assignment-table-create',
  route: {
    path: '/assignment-tables-create',
    parent: assignmentTablesRouteRef,
  },
  page: { title: 'Create assignment table' },
});

export const factTablesRouteRef = createPageRoute({
  id: 'admin-fact-table-index',
  route: { path: '/fact-tables', parent: rootRouteRef },
  page: { title: 'Fact tables' },
});

export const factTablesCreateRouteRef = createPageRoute({
  id: 'admin-fact-table-create',
  route: { path: '/fact-tables-create', parent: factTablesRouteRef },
  page: { title: 'Create fact table' },
});

export const factTableRouteRef = createPageRoute({
  id: 'admin-fact-table',
  route: { path: '/fact-tables/:id', parent: factTablesRouteRef },
  page: { title: 'Fact table' },
});

export const factTableEditRouteRef = createPageRoute({
  id: 'admin-fact-table-edit',
  route: { path: '/fact-tables/:id/edit', parent: factTableRouteRef },
  page: { title: 'Edit fact table' },
});

export const entitiesRouteRef = createPageRoute({
  id: 'admin-entity-index',
  route: { path: '/entities', parent: rootRouteRef },
  page: { title: 'Entities' },
});

export const entityRouteRef = createPageRoute({
  id: 'admin-entity',
  route: { path: '/entities/:id', parent: entitiesRouteRef },
  page: { title: 'Entity' },
});

export const entityRelationTableRouteRef = createPageRoute({
  id: 'admin-entity-relation-table',
  route: {
    path: '/entities/:entityId/entityRelationTables/:entityRelationTableId',
    parent: entityRouteRef,
  },
  page: { title: 'Entity relation table' },
});

export const entityRelationTablesCreateRouteRef = createPageRoute({
  id: 'admin-entity-relation-table-create',
  route: {
    path: '/entity-relation-table-create/:parentEntityId',
    parent: entitiesRouteRef,
  },
  page: { title: 'Create entity relation table' },
});

export const dimensionTablesRouteRef = createPageRoute({
  id: 'admin-dimension-table-index',
  route: { path: '/dimension-tables', parent: rootRouteRef },
  page: { title: 'Dimension tables' },
});

export const dimensionTableRouteRef = createPageRoute({
  id: 'admin-dimension-table',
  route: { path: '/dimension-tables/:id', parent: dimensionTablesRouteRef },
  page: { title: 'Dimension table' },
});

export const dimensionTableRouteEditRef = createPageRoute({
  id: 'admin-dimension-table-edit',
  route: {
    path: '/dimension-tables/:id/edit',
    parent: dimensionTableRouteRef,
  },
  page: { title: 'Edit dimension table' },
});

export const dimensionTableCreateRouteRef = createPageRoute({
  id: 'admin-dimension-table-create',
  route: { path: '/dimension-tables-create', parent: dimensionTablesRouteRef },
  page: { title: 'Create dimension table' },
});

export const labelsRouteRef = createPageRoute({
  id: 'admin-label-index',
  route: { path: '/labels', parent: rootRouteRef },
  page: { title: 'Labels' },
});

export const labelRouteRef = createPageRoute({
  id: 'admin-label',
  route: { path: '/labels/:id', parent: labelsRouteRef },
  page: { title: 'Label' },
});

export const dataAgentRouteRef = createPageRoute({
  id: 'admin-data-agent',
  route: { path: '/data-agent', parent: rootRouteRef },
  page: { title: 'Data agent', defaultTab: 'Documents' },
});

export const dataAgentFactTablesRouteRef = createPageRoute({
  id: 'admin-data-agent-fact-tables',
  route: { path: '/data-agent/fact-tables', parent: dataAgentRouteRef },
  page: { title: 'Fact tables' },
});

export const collectionsRouteRef = createPageRoute({
  id: 'admin-collection-index',
  route: { path: '/collections', parent: rootRouteRef },
  page: { title: 'Collections' },
});

export const collectionRouteRef = createPageRoute({
  id: 'admin-collection',
  route: { path: '/collections/:id', parent: collectionsRouteRef },
  page: { title: 'Collection' },
});

export const agentContextRouteRef = createPageRoute({
  id: 'admin-agent-context',
  route: { parent: rootRouteRef, path: '/agent-context' },
  page: { title: 'Context', defaultTab: 'Global context' },
});

export const agentContextResourcesRouteRef = createPageRoute({
  id: 'admin-agent-context-attached',
  route: { parent: agentContextRouteRef, path: '/agent-context/attached' },
  page: { title: 'Attached context' },
});

export const agentContextAnalyticsRouteRef = createPageRoute({
  id: 'admin-agent-context-analytics',
  route: {
    parent: agentContextRouteRef,
    path: '/agent-context/analytics',
  },
  page: { title: 'Fact tables' },
});

export const googleDocsIntegrationRouteRef = createPageRoute({
  id: 'admin-google-docs',
  route: { path: '/google-docs', parent: rootRouteRef },
  page: { title: 'Google Docs' },
});

export const notionIntegrationRouteRef = createPageRoute({
  id: 'admin-notion',
  route: { path: '/notion', parent: rootRouteRef },
  page: { title: 'Notion' },
});

export const intercomIntegrationRouteRef = createPageRoute({
  id: 'admin-intercom',
  route: { path: '/intercom', parent: rootRouteRef },
  page: { title: 'Intercom' },
});

export const linearIntegrationRouteRef = createPageRoute({
  id: 'admin-linear',
  route: { path: '/linear', parent: rootRouteRef },
  page: { title: 'Linear' },
});

// default routes/routes to be used by other plugins
export const Routes = {
  admin: adminRouteRef,
  resolverServiceConfigurations: resolverServiceConfigurationsRouteRef,
  slack: slackRouteRef,
  policies: policiesRouteRef,
  groups: groupsRouteRef,
  group: groupRouteRef,
  users: usersRouteRef,
  user: userRouteRef,
  invitations: invitationsRouteRef,
  domains: domainsRouteRef,
  agents: agentsRouteRef,
  agent: agentRouteRef,
  apiClients: apiClientsRouteRef,
  apiClient: apiClientRouteRef,
  clients: clientsRouteRef,
  client: clientRouteRef,
  clientSchema: clientsContextSchemaRouteRef,
  roles: rolesRouteRef,
  oauthApps: oauthAppsRouteRef,
  oauthApp: oauthAppRouteRef,
  creditUsage: creditUsageRouteRef,
  metricsDataWarehouse: metricsDataWarehouseRouteRef,
  workflows: workflowsRouteRef,
  workflow: workflowRouteRef,
  workflowInstances: workflowInstancesRouteRef,
  workflowInstanceOverview: workflowInstanceOverviewRouteRef,
  workflowInstanceEvents: workflowInstanceEventsRouteRef,
  workflowLogs: workflowLogsRouteRef,
  workflowTunnels: workflowTunnelsRouteRef,
  flagAppliedConnections: flagAppliedConnectionsRouteRef,
  flagAppliedConnection: flagAppliedConnectionRouteRef,
  eventConnections: eventConnectionsRouteRef,
  eventConnection: eventConnectionRouteRef,
  activityFeeds: activityFeedsRouteRef,
  environments: environmentsRouteRef,
  dataTables: dataTablesRouteRef,
  dataTablesAssignmentTables: dataTablesAssignmentTablesRouteRef,
  dataTablesFactTables: dataTablesFactTablesRouteRef,
  dataTablesDimensionTables: dataTablesDimensionTablesRouteRef,
  assignmentTables: assignmentTablesRouteRef,
  assignmentTable: assignmentTableRouteRef,
  assignmentTablesCreate: assignmentTablesCreateRouteRef,
  assignmentTableEdit: assignmentTablesEditRouteRef,
  factTables: factTablesRouteRef,
  factTable: factTableRouteRef,
  factTablesCreate: factTablesCreateRouteRef,
  factTableEdit: factTableEditRouteRef,
  entities: entitiesRouteRef,
  entity: entityRouteRef,
  entityRelationTable: entityRelationTableRouteRef,
  entityRelationTableCreate: entityRelationTablesCreateRouteRef,
  dimensionTables: dimensionTablesRouteRef,
  dimensionTable: dimensionTableRouteRef,
  dimensionTableEdit: dimensionTableRouteEditRef,
  dimensionTablesCreate: dimensionTableCreateRouteRef,
  github: githubRouteRef,
  cursorIntegration: cursorIntegrationRouteRef,
  claudeAgentsIntegration: claudeAgentsIntegrationRouteRef,
  managedCodingAgent: managedCodingAgentRouteRef,
  labels: labelsRouteRef,
  label: labelRouteRef,
  dataAgent: dataAgentRouteRef,
  dataAgentFactTables: dataAgentFactTablesRouteRef,
  collections: collectionsRouteRef,
  collection: collectionRouteRef,
  agentContext: agentContextRouteRef,
  agentContextResources: agentContextResourcesRouteRef,
  agentContextAnalytics: agentContextAnalyticsRouteRef,
  promptHooks: promptHooksRouteRef,
  promptHook: promptHookRouteRef,
  promptHookDocument: promptHookDocumentRouteRef,
  googleDocsIntegration: googleDocsIntegrationRouteRef,
  intercomIntegration: intercomIntegrationRouteRef,
  linearIntegration: linearIntegrationRouteRef,
  notionIntegration: notionIntegrationRouteRef,
} satisfies Record<string, SubRouteRef | RouteRef>;

export const ExternalRoutes = {
  metrics: metricsExternalRouteRef,
  userSettings: userSettingsExternalRouteRef,
  issue: issueExternalRouteRef,
} satisfies Record<string, ExternalRouteRef>;

const firstPathSegment = (path: string): string => path.split('/')[1] ?? '';

export const adminResourceSegments: string[] = [
  ...new Set(
    Object.values(Routes)
      .flatMap(ref =>
        ref.$$type === '@backstage/SubRouteRef'
          ? [firstPathSegment(ref.path)]
          : [],
      )
      .filter(segment => segment !== ''),
  ),
];

export const adminResourceAliases: ResourceAliasMap = buildResourceAliasMap(
  adminResourceSegments.map(segment => `/admin/${segment}`),
);
